import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowRight, Clock } from 'lucide-react'
import { useDocumentHead } from '@/hooks/useDocumentHead'
import { BlogSidebar } from '@/components/blog/BlogSidebar'
import { resolvePublishedDate } from '@/lib/blogDates'
import { dedupePosts } from '@/lib/blogPosts'
import { sanitizeRichHtml } from '@/lib/sanitizeHtml'
import { faqPageSchemaFromHtml } from '@/lib/extractFaqFromHtml'
import {
  acknowledgePage,
  canonicalPathFor,
  canonicalUrlFor,
  categoryPathFor,
  fetchPage,
  fetchPages,
  languageOf,
  SITE_ORIGIN,
  type BlogPost,
} from '@/lib/blogApi'

/**
 * The blog post detail — /blog/:slug and /blog/category/:category/:slug.
 *
 * Ported from NevTan Sign's BlogPost screen (docusignUi's src/screens/Blog/BlogPost.jsx),
 * rebuilt as Tailwind JSX and as a pure client-side fetch, with a client-side canonical
 * redirect (via useNavigate) standing in for Sign's server-side permanentRedirect.
 */

const RELATED_STEP = 6

const FALLBACK_DESCRIPTION =
  'Guides on running local AI models privately, choosing hardware, and getting the most out of NevTan AI.'

// Filename of an image URL, ignoring query strings (S3 presigned tokens differ per request).
function baseName(url?: string | null): string {
  if (!url) return ''
  return url.split('?')[0].split('#')[0].split('/').pop()?.toLowerCase() || ''
}

// The hero image is already shown above the article, so strip any copies of it out of the
// body HTML to avoid the duplicate.
function removeHeroDuplicates(html: string | null | undefined, heroUrl: string | null): string {
  if (!html) return ''
  const heroBase = baseName(heroUrl)
  if (!heroBase) return html
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    const m = tag.match(/src\s*=\s*["']([^"']+)["']/i)
    return baseName(m ? m[1] : '') === heroBase ? '' : tag
  })
}

function formatDate(value?: string | null): string {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function initials(name?: string | null): string {
  if (!name) return 'N'
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

function thumbOf(p: BlogPost): string | null {
  return p?.heroImageUrl || p?.thumbnailUrl || null
}

function LoadingState() {
  return (
    <div className="nv-container py-20 text-center sm:py-28">
      <p className="text-[14px] text-brand-muted">Loading article…</p>
    </div>
  )
}

function NotFoundState() {
  return (
    <div className="nv-container py-20 text-center sm:py-28">
      <h1 className="nv-h2 text-[1.6rem] sm:text-[2rem]">Article not found</h1>
      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-brand-body">
        This article may have been removed or is not yet published.
      </p>
      <Link to="/blog" className="nv-btn-primary-lg mt-7 inline-flex">
        Back to Blog <ArrowRight size={16} />
      </Link>
    </div>
  )
}

export function BlogPostPage() {
  const { slug, category: categorySlug } = useParams<{ slug: string; category?: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const [page, setPage] = useState<BlogPost | null>(null)
  const [status, setStatus] = useState<'loading' | 'ok' | 'notfound'>('loading')
  const [allPages, setAllPages] = useState<BlogPost[]>([])
  const [relatedShown, setRelatedShown] = useState(RELATED_STEP)

  // Load the full list once — the sidebar (recent, editor's picks, browse-by-topic, search)
  // and the related grid are built from it.
  useEffect(() => {
    let cancelled = false
    fetchPages().then((data) => {
      if (!cancelled) setAllPages(data)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setStatus('loading')
    setPage(null)
    setRelatedShown(RELATED_STEP)

    fetchPage(slug).then((data) => {
      if (cancelled) return
      if (!data) {
        setStatus('notfound')
        return
      }
      setPage(data)
      setStatus('ok')
      acknowledgePage(slug)
    })

    return () => {
      cancelled = true
    }
  }, [slug])

  // A post is addressed by slug, not by its URL's category segment — so an outdated or
  // invented category in the address still resolves the post. Once loaded, redirect to its one
  // canonical address so the article is never indexed at two URLs.
  useEffect(() => {
    if (status !== 'ok' || !page) return
    const canonical = canonicalPathFor(page)
    if (canonical !== location.pathname) navigate(canonical, { replace: true })
  }, [status, page, categorySlug, location.pathname, navigate])

  // Deduped list (same title -> one entry, keeping the copy with an image) used for the
  // related grid and the sidebar so neither shows duplicate posts.
  const dedupedPages = useMemo(() => dedupePosts(allPages), [allPages])

  // Related pool, in descending order of relatedness: same category, then a shared tag, then
  // everything else — each tier by date. Category outranks tags because it is the section the
  // reader deliberately navigated into, where a tag is only a topic hint.
  const relatedPool = useMemo(() => {
    if (!Array.isArray(dedupedPages) || !page) return []
    const tagName = (t: string | { name: string }) => (typeof t === 'string' ? t : t?.name)
    const currentTags = new Set((page.tags || []).map(tagName).filter(Boolean))
    const currentCategory = page.category?.slug || null

    const others = dedupedPages.filter((p) => p && p.slug !== page.slug)
    const byDate = (a: BlogPost, b: BlogPost) =>
      new Date(resolvePublishedDate(b, dedupedPages) || 0).getTime() -
      new Date(resolvePublishedDate(a, dedupedPages) || 0).getTime()

    const sameCategory = currentCategory
      ? others.filter((p) => p.category?.slug === currentCategory)
      : []
    const remaining = others.filter((p) => !sameCategory.includes(p))
    const sharedTag = remaining.filter((p) => (p.tags || []).some((t) => currentTags.has(tagName(t))))
    const restPool = remaining.filter((p) => !sharedTag.includes(p))

    return [...sameCategory.sort(byDate), ...sharedTag.sort(byDate), ...restPool.sort(byDate)]
  }, [dedupedPages, page])

  // Reading time — ~200 words per minute over the plain text of the article.
  const readMinutes = useMemo(() => {
    const text = (page?.htmlContent || '').replace(/<[^>]*>/g, ' ')
    const words = text.trim().split(/\s+/).filter(Boolean).length
    return Math.max(1, Math.round(words / 200))
  }, [page])

  // Hero image: the post's hero, or its thumbnail when no hero is provided.
  const heroSrc = page?.heroImageUrl || page?.thumbnailUrl || null

  // Article HTML: strip the duplicate hero, then sanitize.
  const bodyHtml = useMemo(
    () => sanitizeRichHtml(removeHeroDuplicates(page?.htmlContent, heroSrc)),
    [page, heroSrc],
  )

  const canonical = page ? canonicalUrlFor(page) : `${SITE_ORIGIN}/blog`
  const category = page?.category || null
  // Not a site feature — there is no language switcher. It only tells crawlers and screen
  // readers what language this particular article is written in.
  const language = page ? languageOf(page) : 'en'
  const publishedDate = page ? resolvePublishedDate(page, allPages) : null
  const tags = (Array.isArray(page?.tags) ? page?.tags ?? [] : []).map((t) =>
    typeof t === 'string' ? { name: t, slug: t } : t,
  )
  const related = relatedPool.slice(0, relatedShown)
  const hasMoreRelated = relatedShown < relatedPool.length

  const articleSchema: Record<string, unknown> | null = page
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: page.title,
        description: page.metaDescription,
        datePublished: publishedDate ?? undefined,
        dateModified: page.approvedAt || publishedDate || undefined,
        keywords: page.keyword,
        url: canonical,
        inLanguage: language,
        ...(category ? { articleSection: category.name } : {}),
        publisher: {
          '@type': 'Organization',
          name: 'NevTan AI',
          url: SITE_ORIGIN,
        },
      }
    : null

  // Only present when the post's own body actually contains a heading that reads as a
  // question followed by an answer — see faqPageSchemaFromHtml.
  const faqSchema = page ? faqPageSchemaFromHtml(bodyHtml, canonical) : null

  const jsonLd = articleSchema
    ? ([articleSchema, ...(faqSchema ? [faqSchema] : [])] as Record<string, unknown>[])
    : undefined

  useDocumentHead({
    title: page ? `${page.title} | NevTan AI Blog` : 'NevTan AI Blog',
    description: page?.metaDescription || FALLBACK_DESCRIPTION,
    canonical,
    ogImage: page?.heroImageUrl || page?.thumbnailUrl || undefined,
    jsonLd,
  })

  if (status === 'loading') return <LoadingState />
  if (status !== 'ok' || !page) return <NotFoundState />

  return (
    <div className="nv-container py-10 sm:py-14">
      <nav aria-label="Breadcrumb" className="mb-4 flex flex-wrap items-center gap-2 text-[13px] text-brand-muted">
        <Link to="/" className="hover:text-brand">
          Home
        </Link>
        <span>›</span>
        <Link to="/blog" className="hover:text-brand">
          Blog
        </Link>
        {category && (
          <>
            <span>›</span>
            <Link to={categoryPathFor(category)} className="hover:text-brand">
              {category.name}
            </Link>
          </>
        )}
        <span>›</span>
        <span className="text-brand-heading">{page.title}</span>
      </nav>

      {page.pageType && (
        <span className="nv-eyebrow">{page.pageType}</span>
      )}
      <h1 className="nv-h1 mt-3 text-[1.9rem] leading-tight sm:text-[2.4rem]">{page.title}</h1>

      <div className="mt-9 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
        {/* Main column */}
        <div className="min-w-0">
          {heroSrc && (
            <img
              src={heroSrc}
              alt={page.title}
              className="aspect-video w-full rounded-xl border border-brand-line object-cover shadow-card"
            />
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3 border-b border-brand-line pb-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-tint text-[13px] font-bold text-brand-tint-ink">
              {initials(page.author?.name || 'NevTan AI')}
            </span>
            <span>
              <span className="block text-[13.5px] font-bold text-brand-heading">
                {page.author?.name || 'NevTan AI Team'}
              </span>
              {publishedDate && (
                <span className="block text-[12px] text-brand-muted">{formatDate(publishedDate)}</span>
              )}
            </span>
            <span className="ml-auto flex items-center gap-1.5 text-[12.5px] text-brand-muted">
              <Clock size={14} /> {readMinutes} min read
            </span>
            {tags.length > 0 && (
              <span className="flex w-full flex-wrap gap-1.5">
                {tags.map((t, i) => (
                  <Link
                    key={t?.slug || t?.name || i}
                    to="/blog"
                    className="rounded-lg bg-brand-tint px-2 py-0.5 text-[12px] font-semibold text-brand-tint-ink transition hover:bg-brand-tint/70"
                  >
                    {t?.name}
                  </Link>
                ))}
              </span>
            )}
          </div>

          {/* API blog-template CSS, if present, injected as a real <style> so it is never
              stripped by the sanitizer (it never touches raw article HTML). */}
          {Boolean(page.templateCss) && <style dangerouslySetInnerHTML={{ __html: page.templateCss || '' }} />}
          {/* lang on the article itself tells a screen reader and a crawler that the body of a
              translated post is not in the site's default language. */}
          <article
            className="nv-blog-post mt-6 max-w-none text-[15px] leading-relaxed text-brand-body"
            lang={language}
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />

          {related.length > 0 && (
            <section className="mt-14 border-t border-brand-line pt-10">
              <h2 className="nv-h2 text-[1.3rem]">You May Also Like</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {related.map((p) => {
                  const t = thumbOf(p)
                  return (
                    <Link
                      key={p.slug}
                      to={canonicalPathFor(p)}
                      className="group overflow-hidden rounded-xl border border-brand-line bg-brand-paper shadow-card transition hover:-translate-y-0.5 hover:shadow-raised"
                    >
                      <span className="block aspect-video overflow-hidden bg-brand-tint">
                        {t ? (
                          <img
                            src={t}
                            alt=""
                            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                          />
                        ) : null}
                      </span>
                      <span className="block p-3">
                        <span className="block text-[13px] font-bold leading-snug text-brand-heading">
                          {p.title}
                        </span>
                        <span className="mt-1 block text-[11.5px] text-brand-muted">
                          {formatDate(resolvePublishedDate(p, dedupedPages))}
                        </span>
                      </span>
                    </Link>
                  )
                })}
              </div>
              {hasMoreRelated ? (
                <button
                  type="button"
                  onClick={() => setRelatedShown((n) => n + RELATED_STEP)}
                  className="nv-btn-outline-lg mt-6"
                >
                  See More
                </button>
              ) : (
                <Link
                  to={category ? categoryPathFor(category) : '/blog'}
                  className="nv-btn-outline-lg mt-6 inline-flex"
                >
                  {category ? `View All ${category.name}` : 'View All Posts'}
                </Link>
              )}
            </section>
          )}
        </div>

        {/* Sidebar */}
        <BlogSidebar pages={dedupedPages} current={page} />
      </div>
    </div>
  )
}
